import { Injectable } from "@angular/core";
import Swal, { SweetAlertOptions, SweetAlertResult } from "sweetalert2";

type AlertType = 'success' | 'error' | 'warning' | 'info' | 'question';

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  /**
   * Displays a success alert.
   * @param title Alert title
   * @param message Alert message
   * @param options SweetAlert options
   * @returns Alert result
   */
  public displaySuccess(title: string, message: string, options: Partial<SweetAlertOptions> = {}): Promise<SweetAlertResult> {
    return this.displayAlert('success', title, message, {
      showCancelButton: false,
      ...options
    });
  }

  /**
   * Displays an error alert.
   * @param title Alert title
   * @param message Alert message
   * @param options SweetAlert options
   * @returns Alert result
   */
  public displayError(title: string, message: string, options: Partial<SweetAlertOptions> = {}): Promise<SweetAlertResult> {
    return this.displayAlert('error', title, message, {
      showCancelButton: false,
      ...options
    });
  }

  /**
   * Displays a warning alert.
   * @param title Alert title
   * @param message Alert message
   * @param options SweetAlert options
   * @returns Alert result
   */
  public displayWarning(title: string, message: string, options: Partial<SweetAlertOptions> = {}): Promise<SweetAlertResult> {
    return this.displayAlert('warning', title, message, {
      showCancelButton: false,
      ...options
    });
  }

  /**
   * Displays an info alert.
   * @param title Alert title
   * @param message Alert message
   * @param options SweetAlert options
   * @returns Alert result
   */
  public displayInfo(title: string, message: string, options: Partial<SweetAlertOptions> = {}): Promise<SweetAlertResult> {
    return this.displayAlert('info', title, message, {
      showCancelButton: false,
      ...options
    });
  }

  /**
   * Displays a question alert.
   * @param title Alert title
   * @param message Alert message
   * @param options SweetAlert options
   * @returns Alert result
   */
  public displayQuestion(title: string, message: string, options: Partial<SweetAlertOptions> = {}): Promise<SweetAlertResult> {
    return this.displayAlert('question', title, message, {
      ...options,
      confirmButtonText: 'Oui',
      cancelButtonText: 'Non'
    });
  }

  /**
   * Displays an alert.
   * @param type Alert type
   * @param title Alert title
   * @param message Alert message
   * @param options SweetAlert options
   * @returns Alert result
   */
  public displayAlert(type: AlertType, title: string, message: string, options: Partial<SweetAlertOptions> = {}): Promise<SweetAlertResult> {
    return Swal.fire({
      ...Object.assign({}), // Create a default object first then apply your custom configuration
      ...{
        title: title,
        text: message,
        icon: type,
        buttonsStyling: false,
        customClass: {
          denyButton: 'lpcr-button lpcr-button-danger mx-1',
          closeButton: 'lpcr-button lpcr-button-primary mx-1',
          cancelButton: 'lpcr-button lpcr-button-danger mx-1',
          confirmButton: 'lpcr-button lpcr-button-success mx-1'
        },
        showConfirmButton: true,
        showCancelButton: true,
        allowOutsideClick: false
      },
      ...options
    });
  }

}
